import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors} from '@dnd-kit/core';
import {SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

import {changeOrder} from '../../../../modules/admin/database';
import getDBTypeIcon from '../../../../utils/getDBTypeIcon';
import getDisplayDBType from '../../../../utils/getDisplayDBType';
import styles from './styles/table-list.module.css';

import BoxStatus from '../../../design-system/box/BoxStatus';
import {ReactComponent as DragOrderIcon} from '../../../../assets/design-system/button/drag-order.svg';
import {ReactComponent as PencilIcon} from '../../../../assets/design-system/pencil.svg';
import {ReactComponent as TrashIcon} from '../../../../assets/design-system/trash.svg';

// 드래그 가능한 행 컴포넌트
const SortableRow = ({ dbItem, selectedId, openSidebar, openAddModal, clickTrashIcon, getConnectionStatus, getBoxStatusInfo }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: dbItem.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const { id, aliasName, dbType, state, connectAt } = dbItem;

    const currentConnectionStatus = getConnectionStatus(id);
    
    const dbIconStyle = { width: '20px', height: '20px' };

    return (
        <tr 
            ref={setNodeRef} 
            style={style}
            onClick={() => openSidebar(id)}
            className={isDragging ? styles.dragging : ''}
            data-type={selectedId === dbItem.id ? 'selected' : ''}
        >
            <td></td>
            <td {...attributes} {...listeners}>
                <DragOrderIcon className={styles.dragHandle} />
            </td>
            <td>{aliasName}</td>
            <td>
                <div className={styles.boxType}>
                    {getDBTypeIcon(dbType, dbIconStyle)}
                    <p>{getDisplayDBType(dbType)}</p>
                </div>
            </td>
            <td>{connectAt}</td>
            <td>
                <BoxStatus
                    status={getBoxStatusInfo(currentConnectionStatus, state).status}
                    icon={getBoxStatusInfo(currentConnectionStatus, state).icon}
                    text={getBoxStatusInfo(currentConnectionStatus, state).text}
                />
            </td>
            <td>
                <div className={styles.boxManage}>
                    <PencilIcon className={styles.icon} onClick={(e) => openAddModal(e, dbItem)} />
                    <TrashIcon className={styles.icon} onClick={(e) => clickTrashIcon(e, dbItem)} />
                </div>
            </td>
            <td></td>
        </tr>
    );
};

export default function TableList({ selectedId, openSidebar, response, setCurrentStepName, setConnectionInfo, toggleAddModal, openDeleteModal, getConnectionStatus, getBoxStatusInfo }) {
    const dispatch = useDispatch();

    const { changeResponse, changeError, changeLoading } = useSelector(({ database, loading }) => ({
        changeResponse: database.changeResponse,
        changeError: database.changeError,
        changeLoading: loading['database/CHANGE_ORDER']
    }));
    
    // 드래그된 아이템 관리
    const [localResponse, setLocalResponse] = useState(response);
    const [draggedItem, setDraggedItem] = useState(null);
    
    useEffect(() => {
        setLocalResponse(response);
    }, [response]);
    
    useEffect(() => {
        if (!changeLoading && changeResponse && draggedItem) {
            setDraggedItem(null);
        }

        if (!changeLoading && changeError && draggedItem) {
            setLocalResponse(response);
            setDraggedItem(null);
        }
    }, [changeResponse, changeError, changeLoading, draggedItem, response]);

    const openAddModal = (e, dbItem) => {
        e.stopPropagation(); // 상위 요소로 이벤트 전파 방지

        const { id, dbType, dbName, hostName, portNumber, accessId, password, dbFile, aliasName } = dbItem;

        toggleAddModal();

        setCurrentStepName('info');
        setConnectionInfo({
            id: id ?? null,
            dbType: dbType ?? '',
            dbName: dbName ?? '',
            hostName: hostName ?? '',
            portNumber: portNumber ?? '',
            accessId: accessId ?? '',
            dbPassword: password ?? '',
            dbFile: dbFile ?? null,
            dbFileName: dbFile 
                ? (dbFile.fileName || dbFile.name || '')
                : '',
            aliasName: aliasName ?? ''
        });
    };

    const clickTrashIcon = (e, dbItem) => {
        e.stopPropagation(); // 상위 요소로 이벤트 전파 방지
        openDeleteModal(dbItem.id);
    };

    // 센서 설정 (마우스, 키보드, 터치)
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = localResponse.findIndex(item => item.id === active.id);
            const newIndex = localResponse.findIndex(item => item.id === over.id);
            
            const draggedItemData = localResponse[oldIndex];
            const targetOrder = newIndex + 1;
            
            // 로컬 상태 먼저 업데이트 (즉시 UI 반영)
            const newResponse = arrayMove(localResponse, oldIndex, newIndex);
            setLocalResponse(newResponse);
            
            // 드래그된 아이템 정보 저장
            setDraggedItem({
                id: draggedItemData.id,
                targetOrder,
                oldIndex,
                newIndex
            });
            
            dispatch(changeOrder({ 
                id: draggedItemData.id, 
                targetOrder 
            }));
        }
    };
    
    return (
        <div className={styles.wrapper}>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th></th>
                            <th>순서</th>
                            <th>별칭</th>
                            <th>DB 타입</th>
                            <th>최근 연결 날짜</th>
                            <th>연결 상태</th>
                            <th>관리</th>
                            <th></th>
                        </tr>
                    </thead>
                    <SortableContext
                        items={localResponse ? localResponse.map(item => item.id) : []}
                        strategy={verticalListSortingStrategy}
                    >
                        <tbody>
                            {localResponse && localResponse.length > 0 && localResponse.map((dbItem, index) => (
                                <SortableRow
                                    key={dbItem.id}
                                    dbItem={dbItem}
                                    index={index}
                                    selectedId={selectedId}
                                    openSidebar={openSidebar}
                                    openAddModal={openAddModal}
                                    clickTrashIcon={clickTrashIcon}
                                    getConnectionStatus={getConnectionStatus}
                                    getBoxStatusInfo={getBoxStatusInfo}
                                />
                            ))}
                        </tbody>
                    </SortableContext>
                </table>
            </DndContext>
        </div>
    );
}
